import React, { useMemo, ReactNode } from 'react';
import AgTypography, { styleMappings } from '../Styles/AgTypography';
import { AgColor } from '../Styles/AgColor';
import { FormattedMessage, useIntl } from 'react-intl';
import AgChip from '../Chips/AgChip';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import more_horiz from '../assets/more_horiz.svg';
import { GridRowsProp } from '@mui/x-data-grid';
import { MenuItemData, NestedDropdown } from '../AgMenuItemData';

const mainColors = {
  members: AgColor.turquoiseMain,
  staff: AgColor.purpleMain,
  organizations: AgColor.blueMain,
  certifications: AgColor.grey7,
  generic: AgColor.grey7,
  default: AgColor.secondaryDark,
};
const lightColors = {
  members: AgColor.turquoiseLight,
  staff: AgColor.purpleLight,
  organizations: AgColor.blueLight,
  certifications: AgColor.grey2,
  generic: AgColor.grey2,
  default: AgColor.secondaryLight,
};
export interface NewMenu {
  id?: string;
  show?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  label: string;
  onClick?: () => void;
  subMenus?: Omit<NewMenu, 'subMenus'>[];
}

const AgTableToolbar = ({
  type,
  initialRows,
  rowsCount,
  menuComponents,
  leftElementFilter,
  leftElementColumns,
  addNew,
  date,
  showTableMenu = true,
  newMenus
}:{
  type?: 'members' | 'staff' | 'organizations' | 'certifications' | 'generic' | 'default';
  initialRows?: GridRowsProp;
  rowsCount?: number;
  menuComponents?: ReactNode[],
  newMenus?: MenuItemData,
  leftElementFilter?: ReactNode;
  leftElementColumns?: ReactNode;
  addNew?: ReactNode;
  date?: string;
  showTableMenu?: boolean
}) => {
  const messages = useIntl().formatMessage;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const colors = useMemo(() => ({ main: mainColors[type || 'default'], light: lightColors[type || 'default'] }), [type]);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <Box
      sx={{
        px: 2,
        pt: '3px',
        backgroundColor: AgColor.grey0,
        borderRadius: '8px 8px 0 0',
      }}
    >
      <Box
        sx={{
          ...styleMappings['AP.H5.Medium'],
          color: AgColor.turquoiseMain,
          width: '100%',
          display: 'flex',
          justifyContent:
            leftElementFilter || leftElementColumns ? 'space-between' : 'end',
          alignItems: 'center',
          textTransform: 'capitalize',
          height: '48px',
          bgcolor: AgColor.grey0,
          position: 'sticky',
          top: 0,
          zIndex: 999,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {leftElementFilter}
          {leftElementColumns}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {date && (
            <AgTypography
              contentStyle='AP.Paragraph.Small.Regular'
              style={{ color: AgColor.grey7 }}
            >
              {date}
            </AgTypography>
          )}
          <AgChip
            badgeBGColor={colors.light}
            transparent
            badgeOn
            badgeContent={rowsCount || 0}
            style={{ height: '28px' }}
            type={type}
            label={<FormattedMessage id='SHOWING' />}
          />
          &nbsp;
          <AgTypography
            contentStyle='AP.H5.Medium'
            style={{ color: colors.main }}
          >
            <FormattedMessage id='OF' />
            &nbsp;
          </AgTypography>
          <AgChip
            icon_badge_side='left'
            transparent
            badgeOn
            badgeContent={initialRows?.length || 0}
            style={{ height: '20px' }}
            type={type}
            label={
              type !== 'generic'
                ? messages({ id: (type || '')?.toUpperCase() })
                : ''
            }
          />
          {addNew}
          {showTableMenu && !newMenus ? (
            <Button
              disabled={!(rowsCount && rowsCount > 0)}
              onClick={handleClick}
              sx={{ padding: '0', minWidth: '28px', marginLeft: '16px' }}
            >
              <img
                alt=''
                src={more_horiz}
                style={{
                  border: `1px solid ${AgColor.grey3}`,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '28px',
                  height: '24px',
                }}
              />
            </Button>
          ) : null}
          {/* New menus */}
          {newMenus && showTableMenu ? (
            <NestedDropdown menuItemsData={newMenus} />
          ) : null}
          {!newMenus && showTableMenu && (
            <Menu
              id='demo-positioned-menu'
              aria-labelledby='demo-positioned-button'
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              sx={{
                '& .MuiPaper-root': {
                  boxShadow: '0px 1px 2px 0px #0000000D',
                  border: `1px solid ${AgColor.grey3}`,
                  width: '200px',
                  borderRadius: '8px',
                  padding: '4px 0',
                },
                '& .MuiMenu-list': {
                  padding: '0',
                },
              }}
            >
              {menuComponents?.length &&
                menuComponents?.map((item, index) => {
                  return (
                    <MenuItem
                      onClick={handleClose}
                      key={index}
                      sx={{
                        padding: '0',
                        borderBottom: item ? `1px solid ${AgColor.grey3}` : 0,
                      }}
                    >
                      {item}
                    </MenuItem>
                  );
                })}
            </Menu>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AgTableToolbar;
